import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DrishtiPaywallGate from "@/components/drishti/DrishtiPaywallGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Sun, Moon, Globe, TrendingUp, TrendingDown, BarChart3,
  Target, AlertTriangle, Info, Plus, Edit3, Trash2,
  ChevronRight, Activity, DollarSign, RefreshCw, Minus,
  BookOpen, Eye, Calendar, ChevronDown, Zap, Shield,
  Sparkles, Clock, CheckCircle2, ArrowUpRight, ArrowDownRight,
  Compass, Layers, ExternalLink, Lightbulb, Flame, Award
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n, prefix = "") => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (isNaN(num)) return n;
  const sign = num >= 0 ? "+" : "";
  return `${prefix}${sign}${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const fmtPrice = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (isNaN(num)) return n;
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}`;
};

const BIAS_CONFIG = {
  bullish:  { color: "#00FF88", bg: "rgba(0,255,136,0.08)",  border: "rgba(0,255,136,0.25)", icon: TrendingUp,   label: "Bullish Momentum", score: "78% Bullish" },
  bearish:  { color: "#FF3B30", bg: "rgba(255,59,48,0.08)",  border: "rgba(255,59,48,0.25)",  icon: TrendingDown, label: "Bearish Headwinds", score: "24% Bullish" },
  neutral:  { color: "#94A3B8", bg: "rgba(148,163,184,0.07)", border: "rgba(148,163,184,0.2)", icon: Minus,       label: "Balanced / Neutral", score: "50% Neutral" },
  cautious: { color: "#FFCC00", bg: "rgba(255,204,0,0.07)",  border: "rgba(255,204,0,0.22)",  icon: AlertTriangle, label: "Cautious Volatility", score: "42% Cautious" },
};

const Tooltip = ({ text, children }) => (
  <span className="relative group inline-flex items-center">
    {children}
    <span className="ml-1 cursor-help">
      <Info className="w-3 h-3 text-[#52525B] group-hover:text-[#94A3B8] transition-colors" />
    </span>
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 text-xs bg-[#1C1C22] text-[#94A3B8] border border-white/10 rounded-lg p-2.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
      {text}
    </span>
  </span>
);

// ─── Actionable Stock Card Component ──────────────────────────────────────────
const ActionableStockCard = ({ setup, isLight }) => {
  if (!setup) return null;
  const isObj = typeof setup === "object";
  const sym = isObj ? setup.symbol : setup;
  const name = isObj ? setup.name : sym;
  const sector = isObj ? setup.sector : "Dalal Street Leader";
  const bias = isObj ? setup.bias : "BULLISH";
  const isBull = bias === "BULLISH";
  const action = isObj ? setup.action : (isBull ? "BUY_ABOVE" : "SELL_BELOW");
  const entry = isObj ? setup.entry : null;
  const target = isObj ? setup.target : null;
  const target2 = isObj ? setup.target2 : null;
  const sl = isObj ? setup.sl : null;
  const rr = isObj ? setup.risk_reward : "1:2.4";
  const catalyst = isObj ? setup.catalyst : "High relative strength and volume momentum flagged for today's session.";
  const source = isObj ? setup.source : "Drishti AI Verified";

  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 transition-all duration-200 hover:scale-[1.01] flex flex-col justify-between ${
        isLight
          ? "bg-white border-gray-200 shadow-sm hover:shadow-md"
          : "bg-[#0E0E14] border-white/[0.08] hover:border-white/20"
      }`}
    >
      <div>
        {/* Card Header: Ticker, Sector & Direction Tag */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-base sm:text-lg font-black font-mono tracking-tight ${isLight ? "text-gray-900" : "text-white"}`}>
                {sym}
              </span>
              <Badge
                className={`text-[10px] font-bold py-0.5 px-2 border-0 uppercase ${
                  isBull ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                }`}
              >
                {isBull ? <TrendingUp className="w-2.5 h-2.5 mr-1" /> : <TrendingDown className="w-2.5 h-2.5 mr-1" />}
                {bias}
              </Badge>
            </div>
            <p className={`text-xs mt-0.5 font-medium ${isLight ? "text-gray-500" : "text-[#71717A]"}`}>
              {name} • <span className="text-[#00F0FF]">{sector}</span>
            </p>
          </div>

          <div className="text-right shrink-0">
            <span
              className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-black tracking-wider uppercase font-mono ${
                isBull
                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                  : "bg-red-500/10 text-red-400 border border-red-500/30"
              }`}
            >
              {action.replace(/_/g, " ")}
            </span>
          </div>
        </div>

        {/* Levels Grid (Entry, Target 1, Target 2, Stop Loss, Risk-Reward) */}
        {entry ? (
          <div
            className={`p-3 rounded-xl mb-3.5 grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-center ${
              isLight ? "bg-gray-50 border border-gray-100" : "bg-white/[0.02] border border-white/[0.05]"
            }`}
          >
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-wider mb-0.5 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>
                Trigger Entry
              </p>
              <p className={`text-xs sm:text-sm font-bold font-mono ${isLight ? "text-gray-900" : "text-[#00F0FF]"}`}>
                {fmtPrice(entry)}
              </p>
            </div>
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-wider mb-0.5 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>
                Target 1
              </p>
              <p className="text-xs sm:text-sm font-bold font-mono text-[#00FF88]">
                {fmtPrice(target)}
              </p>
            </div>
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-wider mb-0.5 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>
                Stop Loss (SL)
              </p>
              <p className="text-xs sm:text-sm font-bold font-mono text-[#FF3B30]">
                {fmtPrice(sl)}
              </p>
            </div>
            <div>
              <p className={`text-[10px] uppercase font-bold tracking-wider mb-0.5 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>
                Risk : Reward
              </p>
              <p className="text-xs sm:text-sm font-black font-mono text-[#FFCC00]">
                {rr}
              </p>
            </div>
          </div>
        ) : null}

        {/* Catalyst / Trade Rationale */}
        <div className={`p-2.5 rounded-xl text-xs leading-relaxed mb-3 ${isLight ? "bg-indigo-50/50 text-indigo-950" : "bg-[#6366F1]/[0.06] text-[#A1A1AA]"}`}>
          <div className="flex items-start gap-1.5">
            <Flame className="w-3.5 h-3.5 text-[#00F0FF] shrink-0 mt-0.5" />
            <p className="line-clamp-3">
              <strong className={isLight ? "text-indigo-900" : "text-white"}>Trade Catalyst: </strong>
              {catalyst}
            </p>
          </div>
        </div>
      </div>

      {/* Footer / Quick Actions */}
      <div className="pt-2 border-t border-white/[0.05] flex items-center justify-between text-[11px]">
        <span className={`text-[10px] font-mono ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>
          {source}
        </span>
        <div className="flex items-center gap-2">
          <a
            href={`https://in.tradingview.com/chart/?symbol=NSE:${sym}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 font-semibold hover:underline ${
              isLight ? "text-indigo-600" : "text-[#00F0FF]"
            }`}
          >
            Live Chart <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── Tactical Opening Playbook Component ─────────────────────────────────────
const OpeningPlaybookCard = ({ playbook, isLight }) => {
  const [activeTab, setActiveTab] = useState("gap_up");
  if (!playbook) return null;

  return (
    <div className={`p-4 sm:p-5 rounded-2xl border mb-4 ${isLight ? "bg-white border-gray-200" : "bg-[#0E0E14] border-white/[0.06]"}`}>
      <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00FF88]/10 flex items-center justify-center">
            <Compass className="w-4 h-4 text-[#00FF88]" />
          </div>
          <div>
            <h4 className={`text-sm font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
              Opening Strategy &amp; Dalal Street Playbook
            </h4>
            <p className={`text-[11px] ${isLight ? "text-gray-500" : "text-[#71717A]"}`}>
              Rules for the first 15–30 minutes of market open
            </p>
          </div>
        </div>

        {/* Tab switchers */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-black/20 border border-white/5">
          <button
            onClick={() => setActiveTab("gap_up")}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              activeTab === "gap_up" ? "bg-emerald-500 text-black shadow-md" : "text-[#71717A] hover:text-white"
            }`}
          >
            Gap-Up Rule
          </button>
          <button
            onClick={() => setActiveTab("flat_open")}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              activeTab === "flat_open" ? "bg-amber-400 text-black shadow-md" : "text-[#71717A] hover:text-white"
            }`}
          >
            Flat Open
          </button>
          <button
            onClick={() => setActiveTab("gap_down")}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              activeTab === "gap_down" ? "bg-red-500 text-white shadow-md" : "text-[#71717A] hover:text-white"
            }`}
          >
            Gap-Down Rule
          </button>
        </div>
      </div>

      <div className={`p-3.5 rounded-xl text-xs sm:text-sm leading-relaxed border ${
        activeTab === "gap_up"
          ? isLight ? "bg-emerald-50/70 border-emerald-100 text-emerald-950" : "bg-emerald-500/[0.05] border-emerald-500/20 text-[#D1FAE5]"
          : activeTab === "flat_open"
          ? isLight ? "bg-amber-50/70 border-amber-100 text-amber-950" : "bg-amber-500/[0.05] border-amber-500/20 text-[#FEF3C7]"
          : isLight ? "bg-red-50/70 border-red-100 text-red-950" : "bg-red-500/[0.05] border-red-500/20 text-[#FEE2E2]"
      }`}>
        {playbook[activeTab] || playbook.gap_up || "Follow institutional demand zones and wait for 15-minute price discovery."}
      </div>
    </div>
  );
};

// ─── Pro Tip of the Day Card ─────────────────────────────────────────────────
const ProTipCard = ({ proTip, isLight }) => {
  if (!proTip) return null;
  return (
    <div
      className={`p-4 rounded-2xl border mb-4 flex items-start gap-3.5 relative overflow-hidden ${
        isLight
          ? "bg-amber-50/40 border-amber-200/80 text-amber-950"
          : "bg-gradient-to-r from-amber-500/[0.08] to-amber-600/[0.02] border-amber-500/20 text-[#FDE68A]"
      }`}
    >
      <div className="w-8 h-8 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
        <Award className="w-4 h-4 text-amber-400" />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-amber-400 mb-0.5">
          Deepak Sir's Institutional Trading Rule of the Day
        </p>
        <p className="text-xs sm:text-sm font-medium leading-relaxed italic">
          "{proTip}"
        </p>
      </div>
    </div>
  );
};

// ─── Admin Create/Edit Form ──────────────────────────────────────────────────
const BriefForm = ({ open, onClose, onSaved, existing }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = !!existing;

  const [form, setForm] = useState(() => ({
    brief_type: "pre_market",
    title: "",
    date: today,
    sgx_nifty: "", dow_jones: "", nasdaq: "", gift_nifty: "", crude_oil: "", dollar_index: "",
    nifty_support1: "", nifty_support2: "", nifty_resistance1: "", nifty_resistance2: "",
    banknifty_support1: "", banknifty_support2: "", banknifty_resistance1: "", banknifty_resistance2: "",
    fii_net: "", dii_net: "",
    summary: "", market_bias: "neutral",
    stocks_to_watch: "",
    day_summary: "", alerts_review: "", tomorrow_watchlist: "",
  }));
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleAutoFill = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API}/briefs/preview-generate`,
        { brief_type: form.brief_type, force: true },
        { withCredentials: true }
      );
      const d = res.data?.draft;
      if (d) {
        setForm(f => ({
          ...f,
          title: d.title || f.title,
          date: d.date || f.date,
          market_bias: d.market_bias || f.market_bias,
          sgx_nifty: d.sgx_nifty || f.sgx_nifty,
          dow_jones: d.dow_jones || f.dow_jones,
          nasdaq: d.nasdaq || f.nasdaq,
          gift_nifty: d.gift_nifty || f.gift_nifty,
          crude_oil: d.crude_oil || f.crude_oil,
          dollar_index: d.dollar_index || f.dollar_index,
          nifty_support1: d.nifty_support1 ?? f.nifty_support1,
          nifty_support2: d.nifty_support2 ?? f.nifty_support2,
          nifty_resistance1: d.nifty_resistance1 ?? f.nifty_resistance1,
          nifty_resistance2: d.nifty_resistance2 ?? f.nifty_resistance2,
          banknifty_support1: d.banknifty_support1 ?? f.banknifty_support1,
          banknifty_support2: d.banknifty_support2 ?? f.banknifty_support2,
          banknifty_resistance1: d.banknifty_resistance1 ?? f.banknifty_resistance1,
          banknifty_resistance2: d.banknifty_resistance2 ?? f.banknifty_resistance2,
          fii_net: d.fii_net ?? f.fii_net,
          dii_net: d.dii_net ?? f.dii_net,
          summary: d.summary || f.summary,
          stocks_to_watch: (d.stocks_to_watch || []).join(", "),
          day_summary: d.day_summary || f.day_summary,
          alerts_review: d.alerts_review || f.alerts_review,
          tomorrow_watchlist: (d.tomorrow_watchlist || []).join(", "),
        }));
        toast.success("Form auto-filled with live market data & AI analysis!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch AI market brief");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (existing) {
      setForm({
        brief_type: existing.brief_type ?? "pre_market",
        title: existing.title ?? "",
        date: existing.date ?? today,
        sgx_nifty: existing.sgx_nifty ?? "",
        dow_jones: existing.dow_jones ?? "",
        nasdaq: existing.nasdaq ?? "",
        gift_nifty: existing.gift_nifty ?? "",
        crude_oil: existing.crude_oil ?? "",
        dollar_index: existing.dollar_index ?? "",
        nifty_support1: existing.nifty_support1 ?? "",
        nifty_support2: existing.nifty_support2 ?? "",
        nifty_resistance1: existing.nifty_resistance1 ?? "",
        nifty_resistance2: existing.nifty_resistance2 ?? "",
        banknifty_support1: existing.banknifty_support1 ?? "",
        banknifty_support2: existing.banknifty_support2 ?? "",
        banknifty_resistance1: existing.banknifty_resistance1 ?? "",
        banknifty_resistance2: existing.banknifty_resistance2 ?? "",
        fii_net: existing.fii_net ?? "",
        dii_net: existing.dii_net ?? "",
        summary: existing.summary ?? "",
        market_bias: existing.market_bias ?? "neutral",
        stocks_to_watch: (existing.stocks_to_watch || []).join(", "),
        day_summary: existing.day_summary ?? "",
        alerts_review: existing.alerts_review ?? "",
        tomorrow_watchlist: (existing.tomorrow_watchlist || []).join(", "),
      });
    }
  }, [existing, today]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title || !form.date) { toast.error("Title and date are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        stocks_to_watch: form.stocks_to_watch ? form.stocks_to_watch.split(",").map(s => s.trim()).filter(Boolean) : [],
        tomorrow_watchlist: form.tomorrow_watchlist ? form.tomorrow_watchlist.split(",").map(s => s.trim()).filter(Boolean) : [],
        nifty_support1: form.nifty_support1 ? Number(form.nifty_support1) : null,
        nifty_support2: form.nifty_support2 ? Number(form.nifty_support2) : null,
        nifty_resistance1: form.nifty_resistance1 ? Number(form.nifty_resistance1) : null,
        nifty_resistance2: form.nifty_resistance2 ? Number(form.nifty_resistance2) : null,
        banknifty_support1: form.banknifty_support1 ? Number(form.banknifty_support1) : null,
        banknifty_support2: form.banknifty_support2 ? Number(form.banknifty_support2) : null,
        banknifty_resistance1: form.banknifty_resistance1 ? Number(form.banknifty_resistance1) : null,
        banknifty_resistance2: form.banknifty_resistance2 ? Number(form.banknifty_resistance2) : null,
        fii_net: form.fii_net !== "" ? Number(form.fii_net) : null,
        dii_net: form.dii_net !== "" ? Number(form.dii_net) : null,
      };

      if (isEdit) {
        await axios.put(`${API}/briefs/${existing.brief_id}`, payload, { withCredentials: true });
        toast.success("Brief updated");
      } else {
        await axios.post(`${API}/briefs`, payload, { withCredentials: true });
        toast.success("Brief published to all users!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save brief");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = isLight ? "bg-white border-gray-200 text-gray-900" : "bg-[#18181F] border-white/10 text-white";
  const labelCls = `text-xs font-semibold ${isLight ? "text-gray-600" : "text-[#94A3B8]"}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isLight ? "bg-white border-gray-200" : "bg-[#111118] border-white/10"}`}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className={`font-outfit font-bold text-lg ${isLight ? "text-gray-900" : "text-white"}`}>
              {isEdit ? "Edit Market Brief" : "Create Market Brief"}
            </DialogTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={aiLoading}
              className={`gap-1.5 font-outfit text-xs ${isLight ? "border-indigo-200 text-indigo-600 bg-indigo-50" : "border-indigo-500/30 text-indigo-400 bg-indigo-500/10"}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {aiLoading ? "Fetching AI..." : "AI Auto-Fill"}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Brief Type & Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Brief Type</Label>
              <Select value={form.brief_type} onValueChange={v => set("brief_type", v)} disabled={isEdit}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className={isLight ? "bg-white" : "bg-[#18181F] border-white/10"}>
                  <SelectItem value="pre_market">🌅 Pre-Market (Morning)</SelectItem>
                  <SelectItem value="post_market">🌇 Post-Market (Evening)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Date</Label>
              <Input type="date" value={form.date} onChange={e => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Title */}
          <div>
            <Label className={labelCls}>Headline / Title *</Label>
            <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Nifty Set for Gap-Up Open Near 23,200; Tech Leads" className={inputCls} />
          </div>

          {/* Market Bias */}
          <div>
            <Label className={labelCls}>Market Bias</Label>
            <Select value={form.market_bias} onValueChange={v => set("market_bias", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className={isLight ? "bg-white" : "bg-[#18181F] border-white/10"}>
                <SelectItem value="bullish">🟢 Bullish</SelectItem>
                <SelectItem value="bearish">🔴 Bearish</SelectItem>
                <SelectItem value="neutral">⚪ Neutral / Balanced</SelectItem>
                <SelectItem value="cautious">🟡 Cautious / Range-Bound</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Global Cues */}
          <div>
            <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${isLight ? "text-gray-500" : "text-[#52525B]"}`}>Global Cues</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className={labelCls}>GIFT Nifty</Label>
                <Input value={form.gift_nifty} onChange={e => set("gift_nifty", e.target.value)} placeholder="+0.4% (23,250)" className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Dow Jones</Label>
                <Input value={form.dow_jones} onChange={e => set("dow_jones", e.target.value)} placeholder="+0.32%" className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>NASDAQ</Label>
                <Input value={form.nasdaq} onChange={e => set("nasdaq", e.target.value)} placeholder="+0.85%" className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Crude Oil</Label>
                <Input value={form.crude_oil} onChange={e => set("crude_oil", e.target.value)} placeholder="-0.8% ($82)" className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Dollar Index</Label>
                <Input value={form.dollar_index} onChange={e => set("dollar_index", e.target.value)} placeholder="-0.2% (104.2)" className={inputCls} />
              </div>
            </div>
          </div>

          {/* Nifty & Bank Nifty Levels */}
          <div>
            <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${isLight ? "text-gray-500" : "text-[#52525B]"}`}>Key Technical Levels</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className={`text-xs font-semibold ${isLight ? "text-gray-700" : "text-white"}`}>NIFTY 50</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className={labelCls}>R2</Label><Input type="number" value={form.nifty_resistance2} onChange={e => set("nifty_resistance2", e.target.value)} placeholder="23300" className={inputCls} /></div>
                  <div><Label className={labelCls}>R1</Label><Input type="number" value={form.nifty_resistance1} onChange={e => set("nifty_resistance1", e.target.value)} placeholder="23200" className={inputCls} /></div>
                  <div><Label className={labelCls}>S1</Label><Input type="number" value={form.nifty_support1} onChange={e => set("nifty_support1", e.target.value)} placeholder="23050" className={inputCls} /></div>
                  <div><Label className={labelCls}>S2</Label><Input type="number" value={form.nifty_support2} onChange={e => set("nifty_support2", e.target.value)} placeholder="22950" className={inputCls} /></div>
                </div>
              </div>
              <div className="space-y-2">
                <p className={`text-xs font-semibold ${isLight ? "text-gray-700" : "text-white"}`}>BANK NIFTY</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className={labelCls}>R2</Label><Input type="number" value={form.banknifty_resistance2} onChange={e => set("banknifty_resistance2", e.target.value)} placeholder="49800" className={inputCls} /></div>
                  <div><Label className={labelCls}>R1</Label><Input type="number" value={form.banknifty_resistance1} onChange={e => set("banknifty_resistance1", e.target.value)} placeholder="49500" className={inputCls} /></div>
                  <div><Label className={labelCls}>S1</Label><Input type="number" value={form.banknifty_support1} onChange={e => set("banknifty_support1", e.target.value)} placeholder="49000" className={inputCls} /></div>
                  <div><Label className={labelCls}>S2</Label><Input type="number" value={form.banknifty_support2} onChange={e => set("banknifty_support2", e.target.value)} placeholder="48700" className={inputCls} /></div>
                </div>
              </div>
            </div>
          </div>

          {/* FII / DII */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>FII Net Cash (₹ Cr)</Label>
              <Input type="number" value={form.fii_net} onChange={e => set("fii_net", e.target.value)} placeholder="-1250 (negative = sell)" className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>DII Net Cash (₹ Cr)</Label>
              <Input type="number" value={form.dii_net} onChange={e => set("dii_net", e.target.value)} placeholder="2100 (positive = buy)" className={inputCls} />
            </div>
          </div>

          {/* Analyst Summary */}
          <div>
            <Label className={labelCls}>Analyst Summary / Note</Label>
            <Textarea rows={3} value={form.summary} onChange={e => set("summary", e.target.value)} placeholder="Market opening commentary, global context, and high-probability zones..." className={inputCls} />
          </div>

          {/* Stocks to Watch */}
          <div>
            <Label className={labelCls}>Stocks to Watch (comma separated)</Label>
            <Input value={form.stocks_to_watch} onChange={e => set("stocks_to_watch", e.target.value)} placeholder="TATASTEEL, BHARTIARTL, SBIN, MARUTI" className={inputCls} />
          </div>

          {/* Post-market fields */}
          {form.brief_type === "post_market" && (
            <>
              <div>
                <Label className={labelCls}>Day Summary</Label>
                <Textarea rows={2} value={form.day_summary} onChange={e => set("day_summary", e.target.value)} placeholder="How the market traded today, sector performance..." className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Alert Performance Review</Label>
                <Textarea rows={2} value={form.alerts_review} onChange={e => set("alerts_review", e.target.value)} placeholder="How Drishti alerts performed today..." className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>Tomorrow's Watchlist (comma separated)</Label>
                <Input value={form.tomorrow_watchlist} onChange={e => set("tomorrow_watchlist", e.target.value)} placeholder="NIFTY, BANKNIFTY, TATASTEEL" className={inputCls} />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className={isLight ? "text-gray-500" : "text-[#94A3B8]"}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#6366F1] hover:bg-[#5254cc] text-white font-outfit font-semibold px-6">
              {saving ? "Publishing..." : isEdit ? "Update Brief" : "Publish Brief"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Brief Card (read view) ──────────────────────────────────────────────────
const BriefCard = ({ brief, isAdmin, onEdit, onDelete, isLight }) => {
  const [expanded, setExpanded] = useState(false);
  const bias = BIAS_CONFIG[brief.market_bias] ?? BIAS_CONFIG.neutral;
  const BiasIcon = bias.icon;
  const isPost = brief.brief_type === "post_market";

  const actionableList = brief.actionable_stocks || [];
  const stocksToWatch = brief.stocks_to_watch || [];

  return (
    <div
      className={`rounded-3xl border transition-all duration-300 ${
        isLight ? "bg-white border-gray-200 shadow-sm" : "bg-[#111118] border-white/[0.08]"
      }`}
    >
      {/* Header */}
      <div className="p-5 sm:p-6 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${isPost ? "bg-[#F97316]/15" : "bg-[#00FF88]/15"}`}>
              {isPost ? <Moon className="w-5 h-5 text-[#F97316]" /> : <Sun className="w-5 h-5 text-[#00FF88]" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge className={`text-[10px] font-outfit font-black py-0.5 px-2.5 border-0 tracking-wider ${isPost ? "bg-[#F97316]/15 text-[#F97316]" : "bg-[#00FF88]/15 text-[#00FF88]"}`}>
                  {isPost ? "POST-MARKET REVIEW" : "PRE-MARKET BRIEF"}
                </Badge>
                {brief.is_automated && (
                  <Badge className="text-[10px] font-outfit font-bold py-0.5 px-2 border-0 bg-emerald-500/15 text-emerald-400 flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5 text-emerald-400" /> Drishti AI
                  </Badge>
                )}
                <span className={`text-xs font-mono font-medium ${isLight ? "text-gray-400" : "text-[#71717A]"}`}>
                  {new Date(brief.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              </div>
              <h2 className={`text-base sm:text-lg font-bold font-outfit leading-tight ${isLight ? "text-gray-900" : "text-white"}`}>
                {brief.title}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
            {/* Market Bias Badge */}
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold font-outfit shadow-sm"
              style={{ background: bias.bg, borderColor: bias.border, color: bias.color }}
            >
              <BiasIcon className="w-3.5 h-3.5" />
              <span>{bias.label}</span>
              <span className="text-[10px] opacity-75 font-mono">({bias.score})</span>
            </div>

            {isAdmin && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-[#94A3B8] hover:text-white hover:bg-white/5" onClick={() => onEdit(brief)}>
                  <Edit3 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => onDelete(brief.brief_id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Global Cues HUD */}
      {(brief.sgx_nifty || brief.gift_nifty || brief.dow_jones || brief.nasdaq || brief.crude_oil || brief.dollar_index) && (
        <div className={`mx-5 sm:mx-6 mb-4 p-3.5 rounded-2xl grid grid-cols-3 sm:grid-cols-6 gap-2 ${isLight ? "bg-gray-50 border border-gray-100" : "bg-[#0A0A0C] border border-white/[0.04]"}`}>
          {[
            { label: "GIFT Nifty", val: brief.gift_nifty || brief.sgx_nifty },
            { label: "Dow Jones", val: brief.dow_jones },
            { label: "NASDAQ", val: brief.nasdaq },
            { label: "Crude Oil", val: brief.crude_oil },
            { label: "Dollar Index", val: brief.dollar_index },
            { label: "FII vs DII Delta", val: (brief.fii_net != null && brief.dii_net != null) ? `${(brief.dii_net + brief.fii_net) >= 0 ? '+' : ''}${Math.round(brief.dii_net + brief.fii_net)} Cr` : null },
          ].filter(x => x.val).map(({ label, val }) => (
            <div key={label} className="text-center">
              <p className={`text-[9px] uppercase tracking-wider font-bold mb-0.5 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>{label}</p>
              <p className={`text-xs font-bold font-mono truncate ${
                String(val).includes("+") ? "text-[#00FF88]" : String(val).includes("-") ? "text-[#FF3B30]" : isLight ? "text-gray-700" : "text-[#94A3B8]"
              }`}>{val}</p>
            </div>
          ))}
        </div>
      )}

      {/* Key Technical Levels (Nifty & Bank Nifty) */}
      {(brief.nifty_support1 || brief.banknifty_support1) && (
        <div className="px-5 sm:px-6 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {/* Nifty 50 Ladder */}
          <div className={`p-4 rounded-2xl border ${isLight ? "bg-gray-50 border-gray-100" : "bg-[#0E0E14] border-white/[0.05]"}`}>
            <div className="flex items-center justify-between mb-2.5">
              <span className={`text-xs font-black uppercase tracking-wider font-mono ${isLight ? "text-gray-700" : "text-white"}`}>
                NIFTY 50 • KEY BATTLEGROUNDS
              </span>
              <span className="text-[10px] font-mono text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-0.5 rounded border border-[#00F0FF]/20">
                PIVOT LEVELS
              </span>
            </div>
            <div className="space-y-1.5 text-xs font-mono">
              {brief.nifty_resistance2 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-red-500/[0.06] border border-red-500/10">
                  <span className="text-red-400 font-bold">R2 (Major Supply)</span>
                  <span className="font-bold text-red-400">{fmt(brief.nifty_resistance2)}</span>
                </div>
              )}
              {brief.nifty_resistance1 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-red-500/[0.03]">
                  <span className="text-red-300 font-semibold">R1 (Immediate Hurdle)</span>
                  <span className={`font-bold ${isLight ? "text-gray-800" : "text-white"}`}>{fmt(brief.nifty_resistance1)}</span>
                </div>
              )}
              {brief.nifty_support1 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-emerald-500/[0.03]">
                  <span className="text-emerald-300 font-semibold">S1 (Immediate Demand)</span>
                  <span className={`font-bold ${isLight ? "text-gray-800" : "text-white"}`}>{fmt(brief.nifty_support1)}</span>
                </div>
              )}
              {brief.nifty_support2 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-emerald-500/[0.06] border border-emerald-500/10">
                  <span className="text-emerald-400 font-bold">S2 (Institutional Floor)</span>
                  <span className="font-bold text-emerald-400">{fmt(brief.nifty_support2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bank Nifty Ladder */}
          <div className={`p-4 rounded-2xl border ${isLight ? "bg-gray-50 border-gray-100" : "bg-[#0E0E14] border-white/[0.05]"}`}>
            <div className="flex items-center justify-between mb-2.5">
              <span className={`text-xs font-black uppercase tracking-wider font-mono ${isLight ? "text-gray-700" : "text-white"}`}>
                BANK NIFTY • KEY BATTLEGROUNDS
              </span>
              <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                PIVOT LEVELS
              </span>
            </div>
            <div className="space-y-1.5 text-xs font-mono">
              {brief.banknifty_resistance2 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-red-500/[0.06] border border-red-500/10">
                  <span className="text-red-400 font-bold">R2 (Major Resistance)</span>
                  <span className="font-bold text-red-400">{fmt(brief.banknifty_resistance2)}</span>
                </div>
              )}
              {brief.banknifty_resistance1 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-red-500/[0.03]">
                  <span className="text-red-300 font-semibold">R1 (Immediate Hurdle)</span>
                  <span className={`font-bold ${isLight ? "text-gray-800" : "text-white"}`}>{fmt(brief.banknifty_resistance1)}</span>
                </div>
              )}
              {brief.banknifty_support1 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-emerald-500/[0.03]">
                  <span className="text-emerald-300 font-semibold">S1 (Immediate Demand)</span>
                  <span className={`font-bold ${isLight ? "text-gray-800" : "text-white"}`}>{fmt(brief.banknifty_support1)}</span>
                </div>
              )}
              {brief.banknifty_support2 && (
                <div className="flex justify-between items-center py-1 px-2 rounded bg-emerald-500/[0.06] border border-emerald-500/10">
                  <span className="text-emerald-400 font-bold">S2 (Institutional Floor)</span>
                  <span className="font-bold text-emerald-400">{fmt(brief.banknifty_support2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Institutional FII vs DII Cash Flows */}
      {(brief.fii_net != null || brief.dii_net != null) && (
        <div className="px-5 sm:px-6 mb-4 grid grid-cols-2 gap-3.5">
          {[{ label: "FII Net Flow", val: brief.fii_net }, { label: "DII Net Flow", val: brief.dii_net }].map(({ label, val }) => (
            val != null && (
              <div key={label} className={`flex items-center justify-between p-3.5 rounded-2xl border ${isLight ? "bg-gray-50 border-gray-100" : "bg-[#0E0E14] border-white/[0.05]"}`}>
                <div>
                  <p className={`text-[10px] font-bold uppercase tracking-wide ${isLight ? "text-gray-400" : "text-[#71717A]"}`}>
                    <Tooltip text={label.includes("FII") ? "Foreign Institutional Investors net cash market transaction. Positive means big global funds pumped money into Indian equities." : "Domestic Institutional Investors net cash market transaction. Mutual funds & insurance funds defending the market."}>{label}</Tooltip>
                  </p>
                  <p className={`text-sm sm:text-base font-black font-mono mt-0.5 ${val >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                    ₹{Math.abs(val).toLocaleString("en-IN")} Cr {val >= 0 ? "(BUY)" : "(SELL)"}
                  </p>
                </div>
                {val >= 0 ? <TrendingUp className="w-5 h-5 text-[#00FF88]" /> : <TrendingDown className="w-5 h-5 text-[#FF3B30]" />}
              </div>
            )
          ))}
        </div>
      )}

      {/* Analyst Note / Institutional Commentary */}
      <div className="px-5 sm:px-6 mb-4">
        {brief.summary && (
          <div className={`p-4 rounded-2xl ${isLight ? "bg-indigo-50/60 border border-indigo-100" : "bg-[#6366F1]/[0.06] border border-[#6366F1]/20"}`}>
            <p className="text-xs font-bold uppercase tracking-wider mb-1.5 text-[#00F0FF] flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Institutional Analyst Note
            </p>
            <p className={`text-xs sm:text-sm leading-relaxed ${isLight ? "text-gray-700" : "text-[#CBD5E1]"}`}>
              {brief.summary}
            </p>
          </div>
        )}
      </div>

      {/* Tactical Opening Playbook (Pre-Market) */}
      {brief.opening_playbook && (
        <div className="px-5 sm:px-6">
          <OpeningPlaybookCard playbook={brief.opening_playbook} isLight={isLight} />
        </div>
      )}

      {/* Deepak Sir's Pro Tip of the Day */}
      {brief.pro_tip && (
        <div className="px-5 sm:px-6">
          <ProTipCard proTip={brief.pro_tip} isLight={isLight} />
        </div>
      )}

      {/* ─── ACTIONABLE HIGH-CONVICTION STOCKS TO WATCH ─── */}
      <div className="px-5 sm:px-6 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#00FF88] animate-ping" />
            <h3 className={`text-sm font-black font-outfit uppercase tracking-wider ${isLight ? "text-gray-900" : "text-white"}`}>
              Actionable Setups for Today (Stocks to Watch)
            </h3>
          </div>
          <span className="text-[11px] font-mono text-[#00F0FF]">
            {actionableList.length > 0 ? `${actionableList.length} High-Conviction Trades` : `${stocksToWatch.length} Focus Stocks`}
          </span>
        </div>

        {actionableList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {actionableList.map((setup, idx) => (
              <ActionableStockCard key={idx} setup={setup} isLight={isLight} />
            ))}
          </div>
        ) : stocksToWatch.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {stocksToWatch.map((s, idx) => (
              <ActionableStockCard key={idx} setup={s} isLight={isLight} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#52525B]">No specific stock setups assigned for this brief.</p>
        )}
      </div>

      {/* Post-market extras */}
      {isPost && (brief.day_summary || brief.alerts_review || brief.tomorrow_watchlist?.length > 0) && (
        <div className="px-5 sm:px-6 pb-6">
          <button onClick={() => setExpanded(e => !e)} className={`flex items-center gap-1.5 text-xs font-semibold ${isLight ? "text-gray-500 hover:text-gray-800" : "text-[#71717A] hover:text-[#94A3B8]"} transition-colors`}>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            {expanded ? "Hide" : "Show"} Comprehensive Post-Market Review
          </button>
          {expanded && (
            <div className="mt-3 space-y-3">
              {brief.day_summary && (
                <div className={`p-4 rounded-2xl border ${isLight ? "bg-orange-50/50 border-orange-100" : "bg-[#F97316]/5 border-[#F97316]/15"}`}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-[#F97316]">Full Session Performance</p>
                  <p className={`text-sm leading-relaxed ${isLight ? "text-gray-700" : "text-[#CBD5E1]"}`}>{brief.day_summary}</p>
                </div>
              )}
              {brief.alerts_review && (
                <div className={`p-4 rounded-2xl border ${isLight ? "bg-purple-50/50 border-purple-100" : "bg-[#8B5CF6]/5 border-[#8B5CF6]/15"}`}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-[#8B5CF6]">Drishti AI Alert Review</p>
                  <p className={`text-sm leading-relaxed ${isLight ? "text-gray-700" : "text-[#CBD5E1]"}`}>{brief.alerts_review}</p>
                </div>
              )}
              {brief.tomorrow_watchlist?.length > 0 && (
                <div className="pt-1">
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isLight ? "text-gray-400" : "text-[#52525B]"}`}>📋 Tomorrow's Institutional Watchlist</p>
                  <div className="flex flex-wrap gap-2">
                    {brief.tomorrow_watchlist.map(s => (
                      <span key={s} className={`px-3 py-1.5 rounded-xl text-xs font-bold font-mono border ${isLight ? "bg-blue-50 border-blue-100 text-blue-700" : "bg-[#6366F1]/10 border-[#6366F1]/20 text-[#6366F1]"}`}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Auto-Generate Modal ──────────────────────────────────────────────────
const AutoGenerateModal = ({ open, onClose, onPublished, onPreviewInEditor }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [briefType, setBriefType] = useState("pre_market");
  const [force, setForce] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const handleInstantPublish = async () => {
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/briefs/auto-generate`,
        { brief_type: briefType, force },
        { withCredentials: true }
      );
      toast.success(`⚡ ${briefType === "pre_market" ? "Pre-Market" : "Post-Market"} Brief generated & published with fresh setups!`);
      onPublished();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to auto-generate brief");
    } finally {
      setPublishing(false);
    }
  };

  const handlePreviewInEditor = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API}/briefs/preview-generate`,
        { brief_type: briefType, force: true },
        { withCredentials: true }
      );
      if (res.data?.draft) {
        onPreviewInEditor(res.data.draft);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isLight ? "bg-white border-gray-200" : "bg-[#111118] border-white/10"}`}>
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className={`font-outfit font-bold text-lg ${isLight ? "text-gray-900" : "text-white"}`}>
                Generate Institutional Brief
              </DialogTitle>
              <p className={`text-xs ${isLight ? "text-gray-500" : "text-[#94A3B8]"}`}>
                Screens live market momentum, triggers Drishti alerts, and generates high-profit setups
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-3">
          <div className="space-y-2">
            <Label className={`text-xs font-semibold uppercase tracking-wide ${isLight ? "text-gray-500" : "text-[#94A3B8]"}`}>
              Select Brief Type
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setBriefType("pre_market")}
                className={`p-3.5 rounded-xl border text-left transition-all ${
                  briefType === "pre_market"
                    ? "border-[#00FF88] bg-[#00FF88]/10 text-white"
                    : isLight ? "border-gray-200 bg-gray-50 text-gray-700" : "border-white/10 bg-white/[0.02] text-[#94A3B8]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Sun className="w-4 h-4 text-[#00FF88]" />
                  <span className="font-outfit font-bold text-sm">🌅 Pre-Market</span>
                </div>
                <p className="text-[11px] opacity-75 leading-relaxed">Global cues, Nifty &amp; BankNifty pivots, tactical playbook &amp; fresh trade setups.</p>
              </button>

              <button
                type="button"
                onClick={() => setBriefType("post_market")}
                className={`p-3.5 rounded-xl border text-left transition-all ${
                  briefType === "post_market"
                    ? "border-[#F97316] bg-[#F97316]/10 text-white"
                    : isLight ? "border-gray-200 bg-gray-50 text-gray-700" : "border-white/10 bg-white/[0.02] text-[#94A3B8]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Moon className="w-4 h-4 text-[#F97316]" />
                  <span className="font-outfit font-bold text-sm">🌇 Post-Market</span>
                </div>
                <p className="text-[11px] opacity-75 leading-relaxed">Full session review, institutional cash flows, Drishti alert review &amp; tomorrow's list.</p>
              </button>
            </div>
          </div>

          <div className={`p-3.5 rounded-xl border flex items-start gap-2.5 text-xs ${isLight ? "bg-emerald-50/70 border-emerald-100 text-emerald-900" : "bg-[#00FF88]/[0.06] border-[#00FF88]/15 text-[#D1FAE5]"}`}>
            <Zap className="w-4 h-4 text-[#00FF88] shrink-0 mt-0.5" />
            <span className="leading-relaxed">
              Dynamically screens F&amp;O market movers, pulls active Drishti AI alerts, computes mathematical levels, and structures concrete entry/target/SL trade plans.
            </span>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="rounded border-gray-600 text-[#00FF88] focus:ring-[#00FF88] h-4 w-4 bg-transparent"
            />
            <span className={`text-xs ${isLight ? "text-gray-700" : "text-[#94A3B8]"}`}>
              Overwrite if today's brief already exists (Refreshes with latest live stocks)
            </span>
          </label>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-3 border-t border-white/[0.06]">
            <Button variant="ghost" onClick={onClose} disabled={publishing || previewing} className={isLight ? "text-gray-500" : "text-[#94A3B8]"}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handlePreviewInEditor}
              disabled={publishing || previewing}
              className={`font-outfit font-semibold text-xs h-9 ${isLight ? "border-gray-200 text-gray-700" : "border-white/10 text-white hover:bg-white/5"}`}
            >
              {previewing ? "Fetching..." : "Review in Editor"}
            </Button>
            <Button
              onClick={handleInstantPublish}
              disabled={publishing || previewing}
              className="bg-gradient-to-r from-[#00FF88] to-[#00F0FF] hover:opacity-90 text-black font-outfit font-black text-xs h-9 px-4 gap-1.5 shadow-lg shadow-emerald-500/20"
            >
              <Zap className={`w-3.5 h-3.5 ${publishing ? "animate-spin" : ""}`} />
              {publishing ? "Publishing..." : "⚡ 1-Click Publish Live"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const MarketBriefPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [hasAccess, setHasAccess] = useState(Boolean(isAdmin || user?.has_drishti_access === true));

  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | pre_market | post_market
  const [formOpen, setFormOpen] = useState(false);
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      setHasAccess(true);
      return;
    }
    axios.get(`${API}/drishti/subscription-status`, { withCredentials: true })
      .then(res => {
        setHasAccess(Boolean(res.data?.has_access));
      })
      .catch(() => {
        setHasAccess(false);
      });
  }, [isAdmin, user]);

  const fetchBriefs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/briefs`, { withCredentials: true });
      setBriefs(res.data?.briefs ?? []);
    } catch {
      toast.error("Failed to load market briefs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    if (hasAccess || isAdmin) {
      fetchBriefs(); 
    }
  }, [fetchBriefs, hasAccess, isAdmin]);

  if (!hasAccess && !isAdmin) {
    return (
      <DashboardLayout title="Market Briefs">
        <DrishtiPaywallGate
          featureName="Daily Institutional Market Briefs"
          onAccessGranted={() => {
            setHasAccess(true);
            fetchBriefs();
          }}
        />
      </DashboardLayout>
    );
  }

  // Instant 1-Click Regenerate for Admin
  const handleInstantRegenerate = async () => {
    setRefreshing(true);
    try {
      await axios.post(
        `${API}/briefs/auto-generate`,
        { brief_type: "pre_market", force: true },
        { withCredentials: true }
      );
      toast.success("⚡ Live Market Brief regenerated with fresh stocks!");
      await fetchBriefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to regenerate live brief");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this brief?")) return;
    try {
      await axios.delete(`${API}/briefs/${id}`, { withCredentials: true });
      toast.success("Brief deleted");
      fetchBriefs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = filter === "all" ? briefs : briefs.filter(b => b.brief_type === filter);

  return (
    <DashboardLayout>
      <div className={`min-h-screen ${isLight ? "bg-[#F8F9FA]" : "bg-[#0A0A0C]"}`}>
        {/* Header Bar */}
        <div className={`border-b px-6 py-5 ${isLight ? "bg-white border-gray-200" : "bg-[#0A0A0C] border-white/[0.04]"}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00FF88] to-[#00F0FF] flex items-center justify-center shadow-lg shadow-emerald-500/10">
                  <BarChart3 className="w-5 h-5 text-black" />
                </div>
                <h1 className={`text-xl font-black font-outfit tracking-tight ${isLight ? "text-gray-900" : "text-white"}`}>
                  Drishti Daily Briefs
                </h1>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-semibold font-outfit">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live Market Intelligence
                </div>
              </div>
              <p className={`text-xs sm:text-sm ${isLight ? "text-gray-500" : "text-[#71717A]"}`}>
                Automated pre-market battlegrounds (8:30 AM) &amp; actionable stock setups with defined risk-reward
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchBriefs}
                title="Refresh Briefs"
                className={`h-9 w-9 rounded-xl ${isLight ? "text-gray-400 hover:text-gray-700" : "text-[#52525B] hover:text-white"}`}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>

              {isAdmin && (
                <>
                  <Button
                    onClick={handleInstantRegenerate}
                    disabled={refreshing}
                    className="bg-gradient-to-r from-[#00FF88] to-[#00F0FF] hover:opacity-90 text-black font-outfit font-black h-9 px-3.5 rounded-xl gap-1.5 text-xs shadow-md shadow-emerald-500/15"
                  >
                    <Zap className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                    {refreshing ? "Analyzing Market..." : "⚡ Refresh Live Brief"}
                  </Button>

                  <Button
                    onClick={() => setAutoModalOpen(true)}
                    variant="outline"
                    className={`font-outfit font-semibold h-9 px-3 rounded-xl gap-1.5 text-xs ${isLight ? "border-gray-200 text-gray-700" : "border-white/10 text-white hover:bg-white/5"}`}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                    AI Brief Modal
                  </Button>

                  <Button
                    onClick={() => { setEditing(null); setFormOpen(true); }}
                    variant="outline"
                    className={`font-outfit font-semibold h-9 px-3 rounded-xl gap-1.5 text-xs ${isLight ? "border-gray-200 text-gray-700" : "border-white/10 text-white hover:bg-white/5"}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Manual
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mt-4">
            {[["all", "All Briefs"], ["pre_market", "🌅 Pre-Market (Trade Plans)"], ["post_market", "🌇 Post-Market (Reviews)"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold font-outfit transition-all ${
                  filter === v
                    ? "bg-[#00FF88] text-black shadow-md shadow-emerald-500/20"
                    : isLight ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100" : "text-[#71717A] hover:text-white hover:bg-white/5"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Content list */}
        <div className="p-4 sm:p-6 space-y-5 max-w-7xl mx-auto">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={`h-80 rounded-3xl animate-pulse ${isLight ? "bg-gray-200" : "bg-white/[0.03]"}`} />
            ))
          ) : filtered.length === 0 ? (
            <div className={`rounded-3xl border p-16 text-center ${isLight ? "bg-white border-gray-200" : "bg-[#111118] border-white/[0.06]"}`}>
              <BarChart3 className={`w-12 h-12 mx-auto mb-4 ${isLight ? "text-gray-300" : "text-white/10"}`} />
              <p className={`text-sm font-medium ${isLight ? "text-gray-500" : "text-[#71717A]"}`}>
                {isAdmin ? "No briefs yet. Click '⚡ Refresh Live Brief' to publish today's trade setups!" : "No briefs published yet. Check back soon!"}
              </p>
            </div>
          ) : (
            filtered.map(b => (
              <BriefCard
                key={b.brief_id}
                brief={b}
                isAdmin={isAdmin}
                isLight={isLight}
                onEdit={(b) => { setEditing(b); setFormOpen(true); }}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </div>

      {formOpen && (
        <BriefForm
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={fetchBriefs}
          existing={editing}
        />
      )}

      {autoModalOpen && (
        <AutoGenerateModal
          open={autoModalOpen}
          onClose={() => setAutoModalOpen(false)}
          onPublished={fetchBriefs}
          onPreviewInEditor={(draft) => {
            setEditing(draft);
            setFormOpen(true);
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default MarketBriefPage;
